import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { LockKeyhole } from "lucide-react";

import { pushNavigation } from "../../navigation";
import { useKeysTrustManagedCopy } from "./keysTrustManagedCatalog";

const SLOT_ID = "keys-trust-navigation-slot";

function adminNavigation(): HTMLElement | null {
  return document.querySelector<HTMLElement>(
    '.admin-v12-sidebar nav[data-admin-navigation="primary"]',
  );
}

function suppressUnderlyingActiveRoute(nav: HTMLElement) {
  for (const button of nav.querySelectorAll<HTMLButtonElement>('button.active[aria-current="page"]')) {
    if (button.closest(`#${SLOT_ID}`)) continue;
    button.classList.remove("active");
    button.removeAttribute("aria-current");
  }
}

function keepActiveTrustEntryVisible(slot: HTMLElement) {
  if (typeof slot.scrollIntoView !== "function") return;
  slot.scrollIntoView({ block: "nearest" });
}

export function KeysTrustNavigationPortal({ path }: { path: string }) {
  const { t } = useKeysTrustManagedCopy();
  const [host, setHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!path.startsWith("/admin")) {
      setHost(null);
      return;
    }
    let mountedHost: HTMLElement | null = null;
    const mount = () => {
      const nav = adminNavigation();
      if (!nav) return;
      const existing = document.getElementById(SLOT_ID);
      if (existing instanceof HTMLElement) {
        mountedHost = existing;
        setHost(existing);
        if (path === "/admin/keys-trust") {
          suppressUnderlyingActiveRoute(nav);
          keepActiveTrustEntryVisible(existing);
        }
        return;
      }
      const slot = document.createElement("section");
      slot.id = SLOT_ID;
      slot.setAttribute("data-kavoosh-extension", "keys-trust-navigation");
      nav.appendChild(slot);
      mountedHost = slot;
      setHost(slot);
      if (path === "/admin/keys-trust") {
        suppressUnderlyingActiveRoute(nav);
        keepActiveTrustEntryVisible(slot);
      }
    };
    mount();
    const observer = new MutationObserver(mount);
    const observationRoot = document.getElementById("root") ?? document.body;
    if (path === "/admin/keys-trust") {
      observer.observe(observationRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "aria-current"],
      });
    } else {
      observer.observe(observationRoot, {
        childList: true,
        subtree: true,
      });
    }
    return () => {
      observer.disconnect();
      if (mountedHost?.isConnected) mountedHost.remove();
      setHost(null);
    };
  }, [path]);

  if (!host) return null;
  const active = path === "/admin/keys-trust";
  return createPortal(
    <>
      <p>{t("navGroup")}</p>
      <button
        type="button"
        className={active ? "active" : undefined}
        aria-current={active ? "page" : undefined}
        onClick={() => pushNavigation("/admin/keys-trust")}
      >
        <LockKeyhole size={17} />
        <span>{t("title")}</span>
      </button>
    </>,
    host,
  );
}
